/** Mirrors config/billing.php feature → plans mapping (fallback when API omits plan_features). */
const FEATURE_PLANS = {
  audit_report: ['audit', 'monitor', 'guard'],
  poa: ['poa', 'monitor', 'guard'],
  monitor: ['monitor', 'guard'],
  guard: ['guard'],
};

export const PLANS_SETTINGS_PATH = '/settings#plans';

export function userBypassesPlan(user) {
  return user?.role === 'admin' || user?.role === 'superadmin';
}

export function userPlanActive(user) {
  if (!user) return false;
  if (userBypassesPlan(user)) return true;
  return Boolean(user.plan_active);
}

export function userNeedsSubscription(user) {
  if (!user) return false;
  if (userBypassesPlan(user)) return false;
  return !user.plan_active;
}

export function userCanAccessFeature(user, feature) {
  if (!user || !feature) return false;
  if (userBypassesPlan(user)) return true;

  if (user.plan_features && Object.prototype.hasOwnProperty.call(user.plan_features, feature)) {
    return Boolean(user.plan_features[feature]);
  }

  if (!userPlanActive(user)) return false;

  const plan = user.plan || 'none';
  return (FEATURE_PLANS[feature] || []).includes(plan);
}

export function userHasMonitorAccess(user) {
  return userCanAccessFeature(user, 'monitor');
}

export function userHasAuditReportAccess(user) {
  return userCanAccessFeature(user, 'audit_report');
}

export function userHasPoaAccess(user) {
  return userCanAccessFeature(user, 'poa');
}

/** $197 POA buyers without Monitor/Guard — locked to wizard shell. */
export function isPoaOnlyUser(user) {
  if (!user || userBypassesPlan(user)) return false;
  if (!userPlanActive(user)) return false;
  if (userCanAccessFeature(user, 'monitor')) return false;
  return userCanAccessFeature(user, 'poa');
}

export const POA_ONLY_ALLOWED_PATHS = ['/poa-wizard', '/accounts', '/settings', '/book-call', '/book-nora'];

/** Plans that should book an Account Connection Session after checkout. */
export const BOOKING_REQUIRED_PLANS = ['poa', 'monitor', 'guard'];

export function planRequiresPostPaymentBooking(plan) {
  return BOOKING_REQUIRED_PLANS.includes(String(plan || '').toLowerCase());
}

/** Where to land right after a successful paid checkout (before plan home). */
export function postPaymentPathForUser(user) {
  const plan = user?.plan || 'none';
  if (planRequiresPostPaymentBooking(plan) && userPlanActive(user)) {
    return '/book-call?from=checkout';
  }
  return defaultHomePathForUser(user);
}

/** $27 off first Monitor month after Account Health Audit purchase. */
export function userQualifiesForAuditCredit(user, billing) {
  if (userBypassesPlan(user)) return false;
  if (billing?.audit_credit_eligible === true) return true;
  if (billing?.audit_credit_eligible === false) return false;

  const plan = billing?.plan ?? user?.plan ?? 'none';
  const active = billing?.plan_active ?? user?.plan_active ?? false;
  if (!active || plan === 'monitor' || plan === 'guard') return false;

  return plan === 'audit';
}

/** Where to land after login, signup, or account selection. */
export function defaultHomePathForUser(user) {
  if (userCanAccessFeature(user, 'monitor')) {
    return '/';
  }
  if (userCanAccessFeature(user, 'poa')) {
    return '/poa-wizard';
  }
  if (userCanAccessFeature(user, 'audit_report')) {
    return '/audit-report';
  }
  return '/settings';
}

export function planFeatureFallbackPath(user, requiredFeature) {
  if (requiredFeature === 'monitor') {
    return defaultHomePathForUser(user);
  }
  return '/settings';
}

/** Persist billing fields on the cached user (localStorage). */
export function persistBillingToStoredUser(billing = {}) {
  if (!billing || typeof billing !== 'object') return null;

  let storedUser = null;
  try {
    storedUser = JSON.parse(localStorage.getItem('protego_user') || 'null');
  } catch {
    storedUser = null;
  }

  const updatedUser = {
    ...(storedUser || {}),
    plan: billing.plan ?? storedUser?.plan ?? 'none',
    plan_active: billing.plan_active ?? storedUser?.plan_active ?? false,
    plan_features: billing.plan_features ?? storedUser?.plan_features ?? {},
    plan_label: billing.plan_label ?? storedUser?.plan_label,
    plan_started_at: billing.plan_started_at ?? storedUser?.plan_started_at,
    plan_expires_at: billing.plan_expires_at ?? storedUser?.plan_expires_at,
  };

  try {
    localStorage.setItem('protego_user', JSON.stringify(updatedUser));
  } catch {
    // proceed even if storage fails
  }

  return updatedUser;
}

/** Persist updated plan access and hard-reload into the post-payment booking step (or plan home). */
export function reloadAppForPlanAccess(user, billing = {}) {
  const updatedUser = persistBillingToStoredUser(billing) || {
    ...(user || {}),
    plan: billing.plan ?? user?.plan ?? 'none',
    plan_active: billing.plan_active ?? user?.plan_active ?? false,
    plan_features: billing.plan_features ?? user?.plan_features ?? {},
    plan_label: billing.plan_label ?? user?.plan_label,
  };

  window.location.assign(postPaymentPathForUser(updatedUser));
}
